import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
} from '@mui/material';
import logo from '../../../img/logo.png';
import './OTPVerification.css';

const OTPVerification = ({ backend, subdomain, onVerified }) => {
    const [focusedInput, setFocusedInput] = useState(null);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [timeLeft, setTimeLeft] = useState(600);
    const totalTime = 600;

    useEffect(() => {
        let timer;
        if (isOtpSent && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && isOtpSent) {
            resetOtpProcess();
        }
        return () => clearInterval(timer);
    }, [isOtpSent, timeLeft]);

    const resetOtpProcess = () => {
        setIsOtpSent(false);
        setOtp('');
        setEmail('');
        setTimeLeft(600);
    };

    const formatTime = (seconds) => {
        const m = String(Math.floor(seconds / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleSendOTP = async () => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${backend}${subdomain}/en/policy/send-policy-otp/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ staff_email: email }),
                credentials: 'include',
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data?.error || 'Failed to send OTP');

            setIsOtpSent(true);
            setTimeLeft(600);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (otp.length !== 6 || !/^\d+$/.test(otp)) {
            setError('Please enter a valid 6-digit OTP.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${backend}${subdomain}/en/policy/verify-policy-otp/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ staff_email: email, otp: otp }),
                credentials: 'include',
            });

            const data = await response.json();
            if (!response.ok) {
                setError(data?.error || 'Invalid OTP');
                return;
            }

            sessionStorage.setItem('policy_verified', 'true');
            sessionStorage.setItem('verified_email', email);
            onVerified();
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const progress = (timeLeft / totalTime) * 100;

    return (
        <div
            className="whizz-learning-login-container"
            onKeyDown={(e) => {
                if (e.key === "Enter" && !isLoading) {
                    if (isOtpSent) handleVerifyOTP();
                    else handleSendOTP();
                }
            }}>

            {/* LEFT — Login Form */}
            <div className="login-left-section">
                <div className="login-left-decor"></div>
                <div className="login-form-container">
                    <div className="login-form-box">
                        <h1 className="login-heading">Credentials, Please!</h1>
                        <p className="login-subheading">Unlock the door to smarter compliance vibes</p>

                        <div>
                            <div className="login-input-group">
                                <label className="login-label">UserEmail</label>
                                <input
                                    type="text"
                                    placeholder="Enter your Email"
                                    value={email}
                                    readOnly={isOtpSent}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onFocus={() => setFocusedInput("userEmail")}
                                    className={`login-input ${focusedInput === "userEmail" ? "focused" : ""} ${error && !isOtpSent ? "error" : ""}`}
                                />
                                {error && !isOtpSent && (
                                    <div className="login-error-text">
                                        {error}
                                    </div>
                                )}

                                {isOtpSent && (
                                    <div className="login-change-email-box">
                                        <span className="login-change-email-text">Entered wrong email? </span>
                                        <span
                                            role="button"
                                            className="login-change-email-link"
                                            onClick={resetOtpProcess}
                                        >
                                            Use a different email
                                        </span>
                                    </div>
                                )}

                                {isOtpSent && (
                                    <>
                                        <label className="login-label">Enter OTP</label>
                                        <input
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otp}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d{0,6}$/.test(value)) {
                                                    setOtp(value);
                                                    setError("");
                                                }
                                            }}
                                            onFocus={() => setFocusedInput("otp")}
                                            onBlur={() => setFocusedInput(null)}
                                            className={`login-input ${focusedInput === "otp" ? "focused" : ""} ${error ? "error" : ""}`}
                                        />
                                        {error && (
                                            <p className="login-error-text" style={{ marginTop: "6px", fontSize: "13px" }}>
                                                {error}
                                            </p>
                                        )}
                                    </>
                                )}

                                {isOtpSent && (
                                    <>
                                        <div className="otp-timer-container">
                                            OTP valid for:
                                            <span className="otp-timer-value">
                                                {formatTime(timeLeft)}
                                            </span>
                                        </div>
                                        <div className="otp-progress-bar">
                                            <div className="otp-progress-fill" style={{ width: `${progress}%` }}></div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={isOtpSent ? handleVerifyOTP : handleSendOTP}
                                disabled={isLoading}
                                className="login-button"
                            >
                                {isLoading ? (isOtpSent ? 'Verifying...' : 'Sending OTP...') : (isOtpSent ? 'Verify OTP' : 'Generate OTP')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT — Welcome Panel */}
            <div className="login-right-section">
                <div className="star star-1"></div>
                <div className="star star-2"></div>
                <div className="star star-3"></div>
                <div className="star star-4"></div>
                <div className="star star-5"></div>
                <div className="star star-6"></div>
                <div className="login-content-box">
                    <div className="login-logo-container">
                        <img src={logo} alt="logo" className="login-logo" />
                    </div>
                    <Typography className="login-welcome-heading">Welcome to Policy Acknowledgement</Typography>
                    <p className="login-description">
                        Ensuring our compliance culture remains strong through transparent communication. Please verify your identity to access and acknowledge our standard operating procedures and organization policies.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OTPVerification;
